/**
 * Session Configuration Module
 * 
 * Loads session timeout configuration from environment variables with
 * sensible defaults. Code-enforces security boundaries that cannot be
 * bypassed via configuration.
 * 
 * SECURITY: Minimum 1-hour idle timeout enforced in code (not config).
 * Prevents configuration errors from creating indefinite sessions.
 * Rationale: Stolen device/token should have limited validity window.
 * 
 * SECURITY: Maximum 8-hour session duration enforced as fail-safe.
 * Prevents active run flag from creating indefinite sessions.
 * 
 * @module sessionConfig
 * @see Security Review 052 (F2: Configuration-Based Timeout Bypass)
 */

import { trackEvent } from '../../app-core/analytics/posthog';

/**
 * Session configuration interface
 */
export interface SessionConfig {
  /** Idle timeout in milliseconds (minimum: 1 hour, default: 4 hours) */
  idleTimeoutMs: number;
  /** Whether active runs are exempt from idle timeout */
  activeRunExempt: boolean;
  /** Token refresh threshold as percentage of token lifetime (default: 75%) */
  refreshThresholdPercent: number;
}

/**
 * HARDCODED security constants — NOT configurable via environment.
 * These values are the last line of defense against configuration errors.
 * 
 * @security These constants MUST NOT be derived from environment variables.
 */
export const SESSION_SECURITY_CONSTANTS = Object.freeze({
  /** Minimum idle timeout: 1 hour (HARDCODED FLOOR) */
  MIN_IDLE_TIMEOUT_MS: 3_600_000,
  /** Maximum session duration: 8 hours (HARDCODED CEILING, fail-safe for active runs) */
  MAX_SESSION_DURATION_MS: 8 * 60 * 60 * 1000,
});

/** Default idle timeout: 4 hours */
const DEFAULT_IDLE_TIMEOUT_MS = 14_400_000;

/** Default: active runs exempt from idle timeout */
const DEFAULT_ACTIVE_RUN_EXEMPT = true;

/** Default: refresh token at 75% of lifetime */
const DEFAULT_REFRESH_THRESHOLD_PERCENT = 75;

/**
 * Load session configuration from environment variables with security enforcement.
 * 
 * Environment variables:
 * - VITE_SESSION_IDLE_TIMEOUT_MS: Idle timeout in ms (default: 14400000 = 4 hours)
 * - VITE_SESSION_ACTIVE_RUN_EXEMPT: Whether active runs exempt from timeout (default: true)
 * - VITE_SESSION_REFRESH_THRESHOLD_PERCENT: Token refresh threshold % (default: 75)
 * 
 * @returns SessionConfig with security boundaries enforced
 */
export function getSessionConfig(): SessionConfig {
  // Parse idle timeout from env var
  const rawTimeout = import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MS;
  let configuredTimeout = DEFAULT_IDLE_TIMEOUT_MS;
  
  if (rawTimeout !== undefined && rawTimeout !== '') {
    const parsed = parseInt(String(rawTimeout), 10);
    if (!isNaN(parsed) && parsed > 0) {
      configuredTimeout = parsed;
    }
    // If NaN or <= 0, fall through to default
  }

  // SECURITY: Enforce 1-hour minimum timeout (code-enforced, not config-driven)
  const idleTimeoutMs = Math.max(configuredTimeout, SESSION_SECURITY_CONSTANTS.MIN_IDLE_TIMEOUT_MS);
  
  if (configuredTimeout < SESSION_SECURITY_CONSTANTS.MIN_IDLE_TIMEOUT_MS) {
    if (import.meta.env.DEV) {
      console.warn(
        `⚠️ [Security] Configured timeout ${configuredTimeout}ms is below minimum. ` +
        `Enforcing ${SESSION_SECURITY_CONSTANTS.MIN_IDLE_TIMEOUT_MS}ms.`
      );
    }
    // Track security enforcement event (non-sensitive data only)
    trackEvent('security.config_enforced', {
      control: 'session_timeout_floor',
      configured: configuredTimeout,
      enforced: SESSION_SECURITY_CONSTANTS.MIN_IDLE_TIMEOUT_MS,
    });
  }

  // Parse active run exemption
  const rawActiveRunExempt = import.meta.env.VITE_SESSION_ACTIVE_RUN_EXEMPT;
  const activeRunExempt = rawActiveRunExempt !== undefined
    ? rawActiveRunExempt !== 'false'
    : DEFAULT_ACTIVE_RUN_EXEMPT;

  // Parse refresh threshold
  const rawRefreshThreshold = import.meta.env.VITE_SESSION_REFRESH_THRESHOLD_PERCENT;
  let refreshThresholdPercent = DEFAULT_REFRESH_THRESHOLD_PERCENT;
  if (rawRefreshThreshold !== undefined && rawRefreshThreshold !== '') {
    const parsed = parseInt(String(rawRefreshThreshold), 10);
    if (!isNaN(parsed) && parsed > 0 && parsed < 100) {
      refreshThresholdPercent = parsed;
    }
  }

  return {
    idleTimeoutMs,
    activeRunExempt,
    refreshThresholdPercent,
  };
}
